import { MarkdownViewer } from '@/business-components/markdown-viewer/MarkdownViewer';
import { ParametersCard } from '@/business-components/request-parameter/components/parameters-card/ParametersCard';
import { ResponseExample } from '@/business-components/response-example/ResponseExample';
import { ContainerQuery } from '@/components/container-query/ContainerQuery';
import { CustomTag } from '@/components/custom-tag/CustomTag';
import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import type { HttpApiServerType } from '@/services/http-api/httpApi';
import { ApiDocLayout } from '@/shared/constant/layout/ApiDocLayout';
import { HttpApiContentTypeDefaultValue, ParametersLocationType } from '@tarslib/types/lib/httpApi';
import { ParameterDisplayMode } from '@tarslib/types/lib/projectSetting';
import { Button, Tooltip } from 'aui';
import { FoxIconCode02 } from 'aui-biz-icons';
import clsx from 'clsx';
import React from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import styles from './ResponsePanel.module.css';

export type ResponsePanelProps = {
  value: HttpApiServerType['responses'][number];
};

export function ResponsePanel(props: ResponsePanelProps) {
  const { value } = props;

  const [generatorModalOpen, setGeneratorModalOpen] = React.useState(false);
  const apiDocLayout = useDocsDataModelStore((x) => x.docsBaseConfig.apiDocLayout);
  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);
  const mediaType = React.useMemo(() => {
    return (value.mediaType || HttpApiContentTypeDefaultValue[value.contentType]) ?? undefined;
  }, [value]);

  const responseExampleValue = React.useMemo(() => {
    return [value];
  }, [value]);

  const isNoContent = React.useMemo(() => {
    return value.contentType === 'noContent';
  }, [value.contentType]);

  const isHiddenBodyCardOriginTitle = React.useMemo(() => {
    return !value.description && !value.headers.length;
  }, [value.description, value.headers.length]);

  return (
    <ContainerQuery containerSizes={[880]} className="@container/response-panel">
      <PanelGroup
        direction="horizontal"
        className={clsx('!block', {
          ['@[880px]/response-panel:!flex']: apiDocLayout === ApiDocLayout.OneColumn,
        })}
      >
        <Panel defaultSize={60} minSize={50} maxSize={70} className={clsx('p-4', styles.container)}>
          <div
            className={clsx('pb-2', {
              ['@[880px]/response-panel:pb-4']: apiDocLayout === ApiDocLayout.OneColumn,
            })}
          >
            {(mediaType || isHiddenBodyCardOriginTitle) && (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2 text-sm font-400 text-secondary">
                {mediaType && (
                  <Tooltip title={clientI18n('response.label.contentType')}>
                    <span>
                      <CustomTag className="my-[1px]">{mediaType}</CustomTag>
                    </span>
                  </Tooltip>
                )}

                {isHiddenBodyCardOriginTitle && (
                  <Button
                    type="soft"
                    theme="secondary"
                    className={styles['code-generate-button']}
                    icon={<FoxIconCode02 />}
                    onClick={(e) => {
                      setGeneratorModalOpen(true);
                      e.stopPropagation();
                    }}
                    size="small"
                  >
                    {clientI18n('codeGenerate')}
                  </Button>
                )}
              </div>
            )}

            {value.description && (
              <MarkdownViewer
                className={clsx('', styles.description)}
                value={value.description}
                enableHeaderPlugin={false}
                inDocView={false}
              />
            )}
          </div>

          {!!value.headers.length && (
            <ParametersCard
              viewMode="read"
              type={ParametersLocationType.Header}
              mode={ParameterDisplayMode.MODERN}
              value={value.headers}
              bordered={false}
              title={
                <div className="text-lg font-semibold text-color">
                  {clientI18n('response.label.headers')}
                </div>
              }
            />
          )}

          {isNoContent && <div>{clientI18n('message.hasNoResponseBody')}</div>}

          {value.jsonSchema && !isNoContent && (
            <ParametersCard
              viewMode="read"
              type={ParametersLocationType.Body}
              mode={ParameterDisplayMode.MODERN}
              value={value.jsonSchema}
              bordered={false}
              generatorModalOpen={generatorModalOpen}
              onGeneratorModalOpenChange={setGeneratorModalOpen}
              className={clsx({
                [`${styles['response-panel-body']}`]: isHiddenBodyCardOriginTitle,
              })}
              title={
                <div className="text-lg font-semibold text-color">
                  {clientI18n('response.label.body')}
                </div>
              }
            />
          )}
        </Panel>

        {apiDocLayout === ApiDocLayout.OneColumn && (
          <>
            <PanelResizeHandle className="hidden w-[1px] bg-border-color-split @[880px]/response-panel:block" />

            <Panel
              defaultSize={40}
              className="border-t border-color-split @[880px]/response-panel:border-t-0"
            >
              <ResponseExample
                title={
                  (responseExampleValue[0]?.responseExamples?.length ?? 0) > 1
                    ? clientI18n('examples')
                    : clientI18n('api.label.example')
                }
                value={responseExampleValue}
              />
            </Panel>
          </>
        )}
      </PanelGroup>
    </ContainerQuery>
  );
}
